import React, { useMemo, useState } from 'react';
import dayjs from 'dayjs';
import 'dayjs/locale/es';
import 'dayjs/locale/en';

import {
  Stack,
  Tabs,
  Tab,
  Autocomplete,
  TextField,
  CircularProgress,
  Typography,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { useTranslation } from 'react-i18next';
import { SAD_FEATURE_ENABLED } from '../../../../config/features';

import { useProfesores } from '../../../../hooks/useProfesores';
import DisponibilidadSemanal from './DisponibilidadSemana';
import DiasEspeciales from './DiasEspeciales';
import SADAprobadas from './SADAprobadas';

type TabKey = 'semanal' | 'especiales' | 'sad';

export interface WeeklyContextSnapshot {
  scopeCentro: number | null;
  scopeMask: number;
  formatsExact: boolean;
  vigenciaDesde: string;
  finiteEnd: boolean;
  vigenciaHasta: string;
}

export default function ProfesorDisponibilidadPage() {
  const { t, i18n } = useTranslation<'translation'>();
  const { rows: profes, loading: loadingProf } = useProfesores();

  const [profSel, setProfSel] = useState<{ id: number; nombre: string } | null>(null);
  const [tab, setTab] = useState<TabKey>('semanal');

  // Snapshot de la disponibilidad semanal (para usarlo en "Días especiales")
  const [weeklyRows, setWeeklyRows] = useState<any[]>([]);
  const [weeklyCtx, setWeeklyCtx] = useState<WeeklyContextSnapshot>(() => ({
    scopeCentro: null,
    scopeMask: 1, // BIT_P
    formatsExact: false,
    vigenciaDesde: dayjs().format('YYYY-MM-DD'),
    finiteEnd: false,
    vigenciaHasta: dayjs().add(6, 'month').format('YYYY-MM-DD'),
  }));

  const adapterLocale = useMemo(
    () => (i18n.language?.startsWith('en') ? 'en' : 'es'),
    [i18n.language],
  );

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={adapterLocale}>
      <Stack spacing={3}>
        <Typography
          variant="h5"
          sx={{
            fontWeight: 700,
            color: '#21A598',
            fontFamily: '"Motiva Sans", sans-serif',
            lineHeight: 1.2,
            letterSpacing: '0.5px',
            borderLeft: '6px solid #21A598',
            pl: 1.5,
            mt: 1,
          }}
        >
          {t('disponibilidad.titulo_pagina')}
        </Typography>
        {/* Selector profesor */}
        <Autocomplete
          options={profes}
          loading={loadingProf}
          getOptionLabel={(p) => p.nombre}
          value={profSel}
          isOptionEqualToValue={(o, v) => o.id === v.id}
          onChange={(_, v) => setProfSel(v)}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t('availability.teacherLabel', { defaultValue: 'Profesor/a' })}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loadingProf && <CircularProgress size={20} />}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          sx={{ maxWidth: 380 }}
        />

        {profSel && (
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            sx={{
              '& .MuiTab-root': {
                fontFamily: '"Motiva Sans", sans-serif',
                color: '#666',
                fontWeight: 400,
                transition: 'color 0.2s ease-in-out',
                '&:hover': {
                  color: '#21A598',
                },
                '&:focus': {
                  color: '#21A598',
                },
                '&:active': {
                  color: '#21A598',
                },
              },
              '& .Mui-selected': {
                color: '#21A598 !important',
                fontWeight: 500,
              },
              '& .MuiTabs-indicator': {
                backgroundColor: '#21A598',
                height: '4px',
              },
            }}
          >
            <Tab value="semanal" label={t('disponibilidad.submenu.semanal')} />
            <Tab value="especiales" label={t('disponibilidad.submenu.dias_especiales')} />
            {SAD_FEATURE_ENABLED && (
              <Tab value="sad" label={t('disponibilidad.submenu.sad_aprobadas')} />
            )}
          </Tabs>
        )}

        {profSel && tab === 'semanal' && (
          <DisponibilidadSemanal
            profesorId={profSel.id}
            onRowsChange={setWeeklyRows}
            onContextChange={setWeeklyCtx}
          />
        )}

        {profSel && tab === 'especiales' && (
          <DiasEspeciales
            profesorId={profSel.id}
            bloquesSemana={weeklyRows}
            weeklyContext={weeklyCtx}
          />
        )}

        {SAD_FEATURE_ENABLED && profSel && tab === 'sad' && (
          <SADAprobadas profesorId={profSel.id} />
        )}
      </Stack>
    </LocalizationProvider>
  );
}
